/**
 * STITCH-2 SCOPE 10 (F10) -- the PatientQuickView drawer: demographics + an
 * ops summary + "Open full record". Rendered from the LIST row's own wire
 * fields (demographics only -- the list read carries no phone and no
 * clinical field, so this drawer structurally cannot show either; contact
 * points live solely on the full record view, unchanged).
 */

import { Link } from 'react-router-dom';

import type { PatientListItem } from '@/dashboard/api';
import { formatTimestampIst } from '@/dashboard/owner/Lead360Panel';
import { formatClinicLocalDate } from '@/lib/age';
import {
  genderAgeLabel,
  type PatientGender,
} from '@/dashboard/patients/patient-master-view';
import { AcBadge, DrawerShell } from '@/dashboard/ui';

export const PATIENT_QUICKVIEW_OPEN_FULL = 'Open full record';
export const PATIENT_QUICKVIEW_NO_VISITS = 'No visits yet';

function safeIst(iso: string | null): string | null {
  if (iso === null || iso.trim() === '') return null;
  try {
    return formatTimestampIst(iso);
  } catch {
    return null;
  }
}

/** F-3 (timezone corrective): THE ONE registered-date contract -- TRUE
 *  clinic-local date via the clinic's IANA timezone. Patient 360 renders the
 *  SAME formatter with the SAME clinic timezone, so the two surfaces can never
 *  disagree and a US clinic shows its own local date. */
function safeClinicDate(
  iso: string | null,
  timeZone: string | null | undefined,
): string | null {
  if (iso === null || iso.trim() === '') return null;
  const formatted = formatClinicLocalDate(iso, timeZone);
  return formatted === '' ? null : formatted;
}

export default function PatientQuickView({
  patient,
  clinicLabel,
  onClose,
}: {
  patient: PatientListItem;
  /** Human clinic label from the global context options (never a raw id). */
  clinicLabel: string | null;
  onClose: () => void;
}): JSX.Element {
  const lastVisit = safeIst(patient.lastVisitAt);
  const registered = safeClinicDate(
    patient.createdAt,
    patient.registeredClinicTimezone,
  );
  return (
    <DrawerShell
      eyebrow="Patient quick view"
      title={patient.fullName}
      onClose={onClose}
      testId="patient-quickview"
      footer={
        <Link
          to={`/dashboard/patients/${encodeURIComponent(patient.patientId)}`}
          className="ac-btn ac-btn--primary ac-btn--sm"
          data-testid="patient-quickview-open-full"
        >
          {PATIENT_QUICKVIEW_OPEN_FULL}
        </Link>
      }
    >
      <div className="lead-quickview__facts" data-testid="patient-quickview-facts">
        <div className="lead-quickview__fact">
          <span className="lead-quickview__fact-label">UMR</span>
          <span className="lead-quickview__fact-value">{patient.umr}</span>
        </div>
        <div className="lead-quickview__fact">
          <span className="lead-quickview__fact-label">Patient</span>
          <span className="lead-quickview__fact-value">
            {genderAgeLabel(
              (patient.gender ?? 'unknown') as PatientGender,
              patient.ageYears,
              patient.ageIsApproximate,
            )}
          </span>
        </div>
        {patient.city !== null && patient.city.trim() !== '' ? (
          <div className="lead-quickview__fact">
            <span className="lead-quickview__fact-label">City</span>
            <span className="lead-quickview__fact-value">{patient.city}</span>
          </div>
        ) : null}
        {clinicLabel !== null ? (
          <div className="lead-quickview__fact">
            <span className="lead-quickview__fact-label">Registered at</span>
            <span className="lead-quickview__fact-value">{clinicLabel}</span>
          </div>
        ) : null}
        {registered !== null ? (
          <div className="lead-quickview__fact">
            <span className="lead-quickview__fact-label">Registered on</span>
            <span className="lead-quickview__fact-value">{registered}</span>
          </div>
        ) : null}
        <div className="lead-quickview__fact">
          <span className="lead-quickview__fact-label">Last visit</span>
          <span className="lead-quickview__fact-value" data-testid="patient-quickview-last-visit">
            {lastVisit ?? <AcBadge tone="neutral">{PATIENT_QUICKVIEW_NO_VISITS}</AcBadge>}
          </span>
        </div>
      </div>
      <p className="lead-quickview__readonly">
        Contact details stay on the full record (audited). This quick view is
        demographics only.
      </p>
    </DrawerShell>
  );
}
